/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { KeyboardEvent, useState, useEffect } from "react";
import { InputSearch } from "@nordcloud/gnui";
import { useSleep } from "~/hooks";

type Props = {
  disabled?: boolean;
  placeholder?: string;
  onSubmit: (query: string) => void;
  onChange?: (query: string) => void;
  debounceMs?: number;
  value?: string;
  minLength?: number;
  maxLength?: number;
};

export function SearchInput({
  disabled,
  onSubmit,
  placeholder = "Search",
  onChange,
  debounceMs,
  value,
  minLength,
  maxLength,
}: Props) {
  const { sleep, cancelSleep } = useSleep();
  const [internalValue, setInternalValue] = useState(value ?? "");

  useEffect(() => {
    setInternalValue(value ?? "");
  }, [value]);

  const handleDelayedChange = async (newValue: string, delay: number) => {
    if (!onChange) {
      return;
    }
    cancelSleep();
    await sleep(delay);
    onChange(newValue);
  };

  const onSearch = () => {
    onSubmit(internalValue);
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      onSearch();
    }
  };

  return (
    <InputSearch
      onKeyPress={handleKeyPress}
      value={internalValue}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInternalValue(newValue);
        if (debounceMs) {
          handleDelayedChange(newValue, debounceMs);
        } else {
          onChange?.(newValue);
        }
      }}
      placeholder={placeholder}
      onSearch={onSearch}
      name="search"
      disabled={disabled}
      minLength={minLength}
      maxLength={maxLength}
    />
  );
}
